import React, { useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  useTheme,
} from "@mui/material";
import { AccountCircle, Login, PersonAdd, KeyboardArrowDown, RocketLaunchOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH, REGISTER_ROUTE_PATH } from "../../utils/constants/AppConstants";

const ProfileMenuComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toLoginPage = () => {
    navigate(LOGIN_ROUTE_PATH);
    handleClose();
  };

  const toRegisterPage = () => {
    navigate(REGISTER_ROUTE_PATH);
    handleClose();
  };

  return (
    <>
      <Button
        onClick={handleToggle}
        variant="outlined"
        startIcon={<AccountCircle />}
        endIcon={<KeyboardArrowDown />}
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        sx={{
          borderRadius: 999,
          px: { xs: 1.5, sm: 2 },
          py: 0.7,
          border: `1px solid ${alpha(theme.palette.text.secondary, 0.22)}`,
          backgroundColor: isOpen ? alpha(theme.palette.primary.main, 0.13) : alpha(theme.palette.background.paper, 0.45),
          color: theme.palette.text.primary,
          fontWeight: 600,
          textTransform: "none",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.35),
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            transform: "translateY(-1px)",
          },
        }}
      >
        Account
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 272,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(10px)",
            boxShadow: "0 18px 42px rgba(2,6,23,0.45)",
            overflow: "hidden",
          }
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.4 }}>
          <StackedIntro themeColor={theme.palette.primary.main} />
        </Box>

        <Divider sx={{ borderColor: alpha(theme.palette.text.secondary, 0.16) }} />

        <MenuItem
          onClick={toRegisterPage}
          sx={{ py: 1.25, px: 2, "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) } }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.light, minWidth: 32 }}>
            <PersonAdd fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create Account"
            secondary="Start tracking in under a minute"
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        </MenuItem>

        <MenuItem
          onClick={toLoginPage}
          sx={{ py: 1.25, px: 2, "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.12) } }}
        >
          <ListItemIcon sx={{ color: theme.palette.primary.light, minWidth: 32 }}>
            <Login fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sign In"
            secondary="Continue with your account"
            primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

const StackedIntro = ({ themeColor }: { themeColor: string }) => {
  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
      <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(themeColor, 0.2), color: "primary.light" }}>
        <RocketLaunchOutlined fontSize="small" />
      </Avatar>

      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Your financial dashboard is ready
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
          Sign in or create an account to unlock your dashboard.
        </Typography>
        <Chip
          size="small"
          label="Fast setup"
            sx={{
            mt: 0.9,
            borderRadius: 999,
            fontWeight: 600,
            color: "primary.light",
            backgroundColor: alpha(themeColor, 0.16),
          }}
        />
      </Box>
    </Box>
  );
};

export default ProfileMenuComponent;
